import { FC, useState } from 'react';
import { Form, Typography } from 'antd';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';

import { FormItem, SubmitButton, TextField } from 'componets/form';
import { ModalComponent } from 'componets/common';

import { changePassword } from 'features/userSettings/actions';

import { Rules } from 'utils/constants';
import Utils from 'utils/utils';

import { FieldData, ModalProps } from 'models';
import { ChangePasswordProps } from 'models/api';
import { Rule } from 'models/form';

import { useAppDispatch, useTranslate } from 'hooks';

import s from './index.module.scss';

const PASSWORD_LENGTH = 8;

const PASSWORD_RULES = [Rules.LOWERCASE, Rules.UPPERCASE, Rules.NUMERIC, Rules.REQUIRED];

export const SettingsPasswordModal: FC<ModalProps> = ({ isOpen, onClose }) => {
	const [form] = Form.useForm();
	const [fields, setFields] = useState<FieldData[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const {
		currentPasswordText,
		newPasswordText,
		confirmNewPasswordText,
		changePasswordText,
		changePasswordFormText,
	} = useTranslate([
		'currentPasswordText',
		'newPasswordText',
		'confirmNewPasswordText',
		'changePasswordText',
		'changePasswordFormText',
	]);

	const dispatch = useAppDispatch();

	const onFinish = (data: ChangePasswordProps) => {
		setIsSubmitting(true);
		dispatch(changePassword(data))
			.unwrap()
			.then(() => {
				onClose();
				form.resetFields();
			})
			.catch(({ errors }) => {
				Utils.injectFormErrors(fields, errors, setFields);
				Utils.scrollToFirstError(errors, form);
			})
			.finally(() => {
				setIsSubmitting(false);
			});
	};

	return (
		<ModalComponent isOpen={isOpen} onClose={onClose} title={changePasswordText}>
			<Typography.Text className={s.textModal}>{changePasswordFormText}</Typography.Text>
			<Form
				form={form}
				scrollToFirstError
				onFinish={onFinish}
				onFieldsChange={setFields}
				fields={fields}
			>
				<PasswordRow
					label={currentPasswordText}
					name="currentPass"
					rules={[Rules.REQUIRED]}
					validateTrigger="onSubmit"
				/>

				<PasswordRow
					label={newPasswordText}
					name="newPass"
					rules={[...PASSWORD_RULES, Rules.MIN_LENGTH('newPass', PASSWORD_LENGTH)]}
				/>

				<PasswordRow
					label={confirmNewPasswordText}
					name="confirmPass"
					rules={[
						...PASSWORD_RULES,
						Rules.MIN_LENGTH('confirmPass', PASSWORD_LENGTH),
						Rules.COMPARE_PASSWORDS('newPass'),
					]}
				/>

				<SubmitButton text={changePasswordText} loading={isSubmitting} />
			</Form>
		</ModalComponent>
	);
};

type PasswordRowProps = {
	label: string;
	name: string;
	rules: Rule[];
	validateTrigger?: string;
};

const PasswordRow: FC<PasswordRowProps> = ({ label, name, rules, validateTrigger }) => {
	const [isShow, setIsShow] = useState(false);
	return (
		<div className={s.passwordWrapper}>
			<FormItem
				validateTrigger={validateTrigger}
				className={s.inputPassword}
				name={name}
				rules={rules}
			>
				<TextField label={label} type={isShow ? 'text' : 'password'} />
			</FormItem>

			<button
				className={s.passwordIcon}
				onClick={() => setIsShow((prevState) => !prevState)}
				type="button"
			>
				{isShow ? <EyeOutlined /> : <EyeInvisibleOutlined />}
			</button>
		</div>
	);
};
